/**
 * Arithmetic on stored `YYYY-MM-DD` strings, without going through moment.
 *
 * Every walk over a ticket's window steps a day at a time and asks the calendar a question at each
 * step — which weekday is it, is it a holiday — so what one step costs is what the capacity board's
 * render costs. A moment parse, clone and format per step runs to tens of microseconds; the
 * functions here are integer maths on the day count since 1970-01-01, and a step costs nothing that
 * shows up in a profile.
 *
 * A stored date is a calendar day with no time and no zone, so it is handled as a UTC day: the
 * weekday of `2026-09-21` is Monday wherever the reader sits, and treating the string as UTC
 * midnight is what keeps the machine's own zone out of the answer. Weekday offsets are Monday-first
 * (0 = Monday), the same reading as `moment().weekday()` under the `dow: 1` locale, the `Weekdays`
 * picker and every `weekdays` column.
 */

const MS_PER_DAY = 86_400_000;
/** 1970-01-01 was a Thursday: offset 3 in a Monday-first week. */
const EPOCH_WEEKDAY = 3;
const DAY_STRING = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Whether a value is a stored `YYYY-MM-DD` string, which is the only shape the functions here read.
 *
 * @param value - Anything a caller might hand over as a date.
 * @returns Whether it is a plain calendar day.
 */
export function isDayString(value: string): boolean {
	return DAY_STRING.test(value);
}

/**
 * The day count since 1970-01-01 of a stored date.
 *
 * @param date - A stored `YYYY-MM-DD` string.
 * @returns Whole days since the epoch; negative before it.
 */
export function toDayNumber(date: string): number {
	const year = Number(date.slice(0, 4));
	const month = Number(date.slice(5, 7));
	const day = Number(date.slice(8, 10));
	return Math.round(Date.UTC(year, month - 1, day) / MS_PER_DAY);
}

/**
 * The stored date a day count names.
 *
 * @param dayNumber - Whole days since 1970-01-01.
 * @returns The `YYYY-MM-DD` string.
 */
export function fromDayNumber(dayNumber: number): string {
	return new Date(dayNumber * MS_PER_DAY).toISOString().slice(0, 10);
}

/**
 * A stored date moved by whole days.
 *
 * @param date - A stored `YYYY-MM-DD` string.
 * @param days - How far to move; negative is earlier.
 * @returns The stored date that many days away.
 */
export function shiftDay(date: string, days: number): string {
	return fromDayNumber(toDayNumber(date) + days);
}

/**
 * Whole days from one stored date to another.
 *
 * @param from - The earlier date, usually.
 * @param to - The later date, usually.
 * @returns `to - from` in days; negative when `to` is earlier.
 */
export function daysBetween(from: string, to: string): number {
	return toDayNumber(to) - toDayNumber(from);
}

/**
 * The Monday-first weekday offset of a stored date: 0 is Monday, 6 is Sunday.
 *
 * @param date - A stored `YYYY-MM-DD` string.
 * @returns The offset a `weekdays` column would have to carry for this day to be worked.
 */
export function weekdayOf(date: string): number {
	return (((toDayNumber(date) + EPOCH_WEEKDAY) % 7) + 7) % 7;
}

/**
 * The Monday of a stored date's week.
 *
 * @param date - A stored `YYYY-MM-DD` string.
 * @returns The Monday on or before it.
 */
export function startOfWeekDay(date: string): string {
	return shiftDay(date, -weekdayOf(date));
}

/**
 * The Sunday of a stored date's week.
 *
 * @param date - A stored `YYYY-MM-DD` string.
 * @returns The Sunday on or after it.
 */
export function endOfWeekDay(date: string): string {
	return shiftDay(date, 6 - weekdayOf(date));
}
